import { jwtDecode } from 'jwt-decode';

// Simple token storage management
const TOKENS_KEY = 'api_tokens';
const AUTH_KEY = 'auth_state';
// Survives the full page navigation clearAuth performs, so the login screen can
// explain why the user is suddenly looking at it. sessionStorage rather than
// local: the note belongs to this one redirect, not to the browser profile.
const LOGOUT_REASON_KEY = 'auth_logout_reason';

export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please login again.';

const getTokens = () => {
  try {
    return JSON.parse(localStorage.getItem(TOKENS_KEY) || 'null');
  } catch {
    return null;
  }
};

const setTokens = (tokens) => {
  localStorage.setItem(TOKENS_KEY, JSON.stringify(tokens));
};

// Set for the rest of the page's life once a logout is under way. A screen
// normally has several requests in flight at once, and when the session dies
// they all fail together -- without this, each one would queue its own redirect
// and overwrite the reason the first one left.
let loggingOut = false;

const clearAuth = (reason = SESSION_EXPIRED_MESSAGE) => {
  if (loggingOut) return;
  loggingOut = true;

  localStorage.removeItem(TOKENS_KEY);
  localStorage.removeItem(AUTH_KEY);

  try {
    sessionStorage.setItem(LOGOUT_REASON_KEY, reason);
  } catch {
    // Storage can be unavailable (private mode, blocked site data). The
    // redirect is the part that matters; the message is a courtesy.
  }

  window.location.href = '/auth/login';
};

// Check if token expires within 5 minutes
const isExpired = (token) => {
  if (!token) return true;
  try {
    const { exp } = jwtDecode(token);
    return Date.now() >= exp * 1000 - 5 * 60 * 1000;
  } catch {
    return true;
  }
};

// Refresh lock to prevent concurrent refresh calls
let refreshPromise = null;

// Refresh tokens
const refresh = async (skipRedirectOnError = false) => {
  const tokens = getTokens();
  if (!tokens?.refreshToken) throw new Error('No refresh token');

  try {
    const { refreshToken } = await import('../api');
    const response = await refreshToken(tokens.refreshToken);

    // Handle backend response structure from successResponse()
    // Response format: { success: true, data: { idToken, accessToken, ... }, timestamp }
    if (!response?.data?.data) {
      throw new Error('Invalid refresh response format');
    }

    const tokenData = response.data.data; // Extract the actual token data

    // Validate required fields from backend
    if (!tokenData.idToken || !tokenData.accessToken) {
      throw new Error('Missing tokens in refresh response');
    }

    const newTokens = {
      idToken: tokenData.idToken,
      accessToken: tokenData.accessToken,
      refreshToken: tokenData.refreshToken || tokens.refreshToken, // Keep old if not rotated
      expiresIn: tokenData.expiresIn,
      tokenType: tokenData.tokenType || 'Bearer',
    };

    setTokens(newTokens);
    return newTokens.idToken;
  } catch (error) {
    // Handle specific backend error codes silently
    // Clear auth and redirect for any refresh failure (skip if testing)
    if (!skipRedirectOnError) {
      clearAuth();
    }
    throw new Error('Session expired. Please login again.');
  }
};

// Refresh with lock to prevent race conditions
const refreshWithLock = async (skipRedirectOnError = false) => {
  if (!refreshPromise) {
    refreshPromise = refresh(skipRedirectOnError).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// Main function: Get current valid token
export const getCurrentToken = async () => {
  try {
    const tokens = getTokens();

    if (!tokens?.idToken) {
      throw new Error('No authentication token found. Please login again.');
    }

    if (isExpired(tokens.idToken)) {
      return await refreshWithLock();
    }

    return tokens.idToken;
  } catch (error) {
    // Only end a session that actually exists.
    //
    // Anonymous visitors browse public pages that call authenticated endpoints
    // -- picking a ULB on /newreports fetches the CTU/CPS event lists through
    // getAllEventsByQueryApi, which comes through here. With no token this
    // threw, and clearAuth() bounced the visitor to /auth/login with "your
    // session has expired" before any request was even sent. The caller's
    // catch could not undo it: clearAuth navigates as a side effect.
    //
    // Same guard the 401 interceptor in src/api/index.js already applies.
    // A genuine expiry still logs out -- refresh() calls clearAuth() itself.
    if (hasStoredSession()) {
      clearAuth();
    }

    // Re-throw with user-friendly message
    throw new Error('Authentication failed. Please login again.');
  }
};

// Safe version for interceptors - doesn't redirect, just returns null on failure
export const getCurrentTokenSafe = async () => {
  try {
    const tokens = getTokens();

    if (!tokens?.idToken) {
      return null;
    }

    if (isExpired(tokens.idToken)) {
      return await refreshWithLock(true); // Skip redirect on error
    }

    return tokens.idToken;
  } catch (error) {
    // Don't redirect, just return null
    return null;
  }
};

// Get all tokens
export const getAllTokens = async () => {
  const idToken = await getCurrentToken();
  const tokens = getTokens();
  return { idToken, ...tokens };
};

// Check if authenticated
export const isAuthenticated = () => {
  const tokens = getTokens();
  const authState = localStorage.getItem(AUTH_KEY);
  return !!(tokens?.idToken && authState);
};

// Clear auth data
export const clearAuthData = clearAuth;

/**
 * Log the user out of a session the server has rejected.
 *
 * Called by the 401 response interceptors in src/api/index.js. Safe to call
 * repeatedly -- only the first call in a page's lifetime does anything.
 */
export const forceLogout = (reason) => clearAuth(reason);

/**
 * Is there a stored session to lose?
 *
 * Anonymous visitors browse the public pages with no tokens at all, and the
 * pre-login screens call endpoints that answer 401 as a matter of course.
 * Neither should be redirected anywhere.
 */
export const hasStoredSession = () => !!getTokens()?.idToken;

/**
 * Read and clear the note left behind by the last forced logout. Returns null
 * when the user reached the login screen on their own.
 */
export const consumeLogoutReason = () => {
  try {
    const reason = sessionStorage.getItem(LOGOUT_REASON_KEY);
    if (reason) sessionStorage.removeItem(LOGOUT_REASON_KEY);
    return reason;
  } catch {
    return null;
  }
};

// Store tokens (for login)
export const storeTokens = setTokens;
